import {
    Bar,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
    ResponsiveContainer,
    BarChart,
} from "recharts";

import Typography from "@/ui/atoms/Typography";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { FileUploadAnalytics } from "@/ui/organisms/dashboardTypes";
import { CHART_COLORS } from "@/ui/organisms/dashboardTypes";

import { createSortByNumberPropertyComparator } from "@/utils/sort";

import { months } from "../createViewDashboard";

const compareByValueDesc = createSortByNumberPropertyComparator(
    "value",
    "desc",
);

export const retrieveFileAnalyticsNodes = (
    fileAnalytics: FileUploadAnalytics,
    currentMonth: number,
) => {
    const filesByUser = Object.entries(fileAnalytics.filesByUser)
        .map(([user, count]) => ({ name: user, value: count }))
        .toSorted(compareByValueDesc);

    const filesByType = [
        {
            name: "Excel",
            value: fileAnalytics.filesByFileType["excel"],
        },
        {
            name: "PDF",
            value: fileAnalytics.filesByFileType["pdf"],
        },
    ];

    const lineData = Array.from({ length: 12 }, (_, i) => {
        const monthIndex = (currentMonth - (11 - i) + 12) % 12;
        return {
            month: months[monthIndex],
            value: fileAnalytics.filesUploadedOverTime[monthIndex + 1] ?? 0,
        };
    });

    return fileAnalytics.totalFilesUploaded > 0
        ? [
              {
                  element: (
                      <>
                          <Typography variant="h1">
                              Total files uploaded:{" "}
                              {fileAnalytics.totalFilesUploaded}
                          </Typography>
                          <Typography variant="h1">
                              Files last 24h:{" "}
                              {fileAnalytics.filesUploadedLast24h}
                          </Typography>
                          <Typography variant="h1">
                              Files last 7d: {fileAnalytics.filesUploadedLast7d}
                          </Typography>
                      </>
                  ),
                  options: {
                      i: "File Analytics",
                      w: 2,
                      h: 2,
                      x: 0,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              File Types Distribution
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <PieChart>
                                  <Pie
                                      data={filesByType}
                                      dataKey="value"
                                      nameKey="name"
                                      outerRadius={100}
                                      fill="#8884d8"
                                  >
                                      {filesByType.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Pie>
                                  <Legend
                                      verticalAlign="bottom"
                                      height={36}
                                      align="right"
                                  />
                              </PieChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "File Type Analytics",
                      w: 2,
                      h: 2,
                      x: 2,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Number ofFiles by User
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={filesByUser}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {filesByUser.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User File Chart",
                      w: 2,
                      h: 2,
                      x: 4,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Extractions over time
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <LineChart data={lineData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" />
                                  <YAxis />
                                  <Tooltip />
                                  <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="#8884d8"
                                  />
                              </LineChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Deal Chart",
                      w: 6,
                      h: 2,
                      x: 0,
                      y: 4,
                  },
              },
          ]
        : [
              {
                  element: (
                      <LayoutFillParentCentered>
                          <Typography variant="h1">No Files Found</Typography>
                      </LayoutFillParentCentered>
                  ),
                  options: {
                      i: "No Files Uploaded",
                      w: 100,
                      h: 2,
                      x: 0,
                      y: 0,
                      boxed: false,
                  },
              },
          ];
};
